import { types } from "../actions";
import { combineReducers } from "redux";
import { ADJUSTMENT_PERIODS } from "./../../constants";

const isConfigured = (state = false, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_IS_FINANCE_CONFIGURED:
      return Boolean(payload);
    case types.RESET:
      return false;
    default:
      return state;
  }
};

const financeType = (state = "CASH", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_FINANCE_TYPE:
      return ["CASH", "CFA", "CFNZ"].includes(payload) ? payload : "CASH";
    case types.RESET:
      return "CASH";
    default:
      return state;
  }
};

const financeSubtype = (state = "RENTAL", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_FINANCE_SUBTYPE:
      // Normalize "RENT" to "RENTAL"
      return payload === "RENT" ? "RENTAL" : payload;
    case types.SET_QUOTE_FINANCE_TYPE:
      return payload === "CFA" ? state || "RENTAL" : state;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return "RENTAL";
    default:
      return state;
  }
};

const originatorReference = (state = "", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return payload || "";
    case types.RESET:
      return "";
    default:
      return state;
  }
};

const canonCopyPlanType = (state = "Based on Combined", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CANON_COPY_PLAN_TYPE:
      return [
        "Based on Combined",
        "Based on Color",
        "Based on B&W",
        "Based on Two Minimums"
      ].includes(payload)
        ? payload
        : "Based on Combined";
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return "Based on Combined";
    default:
      return state;
  }
};

const cfaAmount = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_AMOUNT:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const totalAmountFinanced = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_TOTAL_AMOUNT_FINANCED:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const adjustmentPeriod = (state = "1_MONTH", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_ADJUSTMENT_PERIOD:
      return ADJUSTMENT_PERIODS.map(({ value }) => value).includes(payload)
        ? payload
        : "1_MONTH";
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return "1_MONTH";
    default:
      return state;
  }
};

const canonFinancePayout = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CANON_FINANCE_PAYOUT:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const thirdPartyPayout = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_THIRD_PARTY_PAYOUT:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcBw = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_BW:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcBwAdjustClickChargePerMonthExclGst = (
  state = 0,
  { type, payload }
) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_BW_ADJUST_CLICK_CHARGE_PER_MONTH_EXCL_GST:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcBwMinimumMonthlyClickVolume = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_BW_MINIMUM_MONTHLY_CLICK_VOLUME:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcBwServiceCopyCost = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_BW_SERVICE_COPY_COST:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcBwExcessFinancedCopyCost = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_BW_EXCESS_FINANCED_COPY_COST:
      return Number(Math.abs(payload).toFixed(4)) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcColour = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_COLOUR:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcColourAdjustClickChargePerMonthExclGst = (
  state = 0,
  { type, payload }
) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_COLOUR_ADJUST_CLICK_CHARGE_PER_MONTH_EXCL_GST:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcColourMinimumMonthlyClickVolume = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_COLOUR_MINIMUM_MONTHLY_CLICK_VOLUME:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcColourServiceCopyCost = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_COLOUR_SERVICE_COPY_COST:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const cfaCcColourExcessFinancedCopyCost = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_CFA_CC_COLOUR_EXCESS_FINANCED_COPY_COST:
      return Number(Math.abs(payload).toFixed(4)) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const financeTerm = (state = 12, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_FINANCE_TERM:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 12;
    default:
      return state;
  }
};

const instalmentAmount = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_INSTALMENT_AMOUNT:
      return Number(payload) || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const instalmentType = (state = "MONTHLY", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_INSTALMENT_TYPE:
      payload = String(payload).toUpperCase();
      return ["MONTHLY", "QUARTERLY", "YEARLY"].includes(payload)
        ? payload
        : "MONTHLY";
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return "MONTHLY";
    default:
      return state;
  }
};

const payableIn = (state = "ADVANCE", { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_PAYABLE_IN:
      payload = String(payload).toUpperCase();
      return ["ADVANCE", "ARREARS"].includes(payload) ? payload : "ADVANCE";
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return "ADVANCE";
    default:
      return state;
  }
};

const residualValue = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_RESIDUAL_VALUE:
      return Math.round(Number(payload) * 100) / 100 || 0;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const deferredPayment = (state = 0, { type, payload }) => {
  switch (type) {
    case types.SET_QUOTE_FINANCE_DEFERRED_PAYMENT:
      return payload;
    case types.RESET:
    case types.SET_QUOTE_ORIGINATOR_REFERENCE:
      return 0;
    default:
      return state;
  }
};

const payoutInfo = (state = [], { type, payload }) => {
  const initialValue = [
    {
      SupplierOrFinancer: "",
      ContractIdentifier: "",
      PayoutFunds: ""
    }
  ];
  switch (type) {
    case types.SET_QUOTE_PAYOUT_INFO:
      return payload;
    case types.RESET:
      return initialValue;
    default:
      //Initial value to start with
      if (state.length < 1) {
        return initialValue;
      }
      return state;
  }
};

export default combineReducers({
  // region Common
  isConfigured,

  financeType,
  financeSubtype,

  totalAmountFinanced,
  cfaAmount,

  canonFinancePayout,
  thirdPartyPayout,

  financeTerm,
  instalmentAmount,
  instalmentType,

  deferredPayment,
  // endregion

  // Originator Only
  originatorReference,

  payableIn,

  // Lease Only
  residualValue,

  // region PPP Only
  adjustmentPeriod,
  canonCopyPlanType,
  cfaCcBw,
  cfaCcBwServiceCopyCost,
  cfaCcBwMinimumMonthlyClickVolume,
  cfaCcBwAdjustClickChargePerMonthExclGst,
  cfaCcBwExcessFinancedCopyCost,
  cfaCcColour,
  cfaCcColourServiceCopyCost,
  cfaCcColourMinimumMonthlyClickVolume,
  cfaCcColourAdjustClickChargePerMonthExclGst,
  cfaCcColourExcessFinancedCopyCost,
  payoutInfo
  // endregion
});
